// §34.3 - presentational day-cell rendering shared by the 2-week strip and
// month views. Kept free of data fetching so it's directly unit-testable
// with a fabricated day list (see AvailabilityGrid.test.tsx).
import { useTranslation } from 'react-i18next';
import type { DayAvailabilityDto } from '@wrapforce/shared';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';

const STATE_STYLE: Record<DayAvailabilityDto['state'], string> = {
  available: 'border-success/30 bg-success/10 text-success hover:bg-success/20',
  tight: 'border-warning/30 bg-warning/10 text-warning hover:bg-warning/20',
  full: 'border-danger/20 bg-danger/10 text-danger opacity-60 cursor-not-allowed',
  closed: 'border-border bg-surface-2 text-muted opacity-60 cursor-not-allowed',
};

export function AvailabilityGrid({
  days, selectedDate, preferredDate, onSelectDay, layout,
}: {
  days: DayAvailabilityDto[];
  selectedDate: string | null;
  preferredDate: string | null;
  onSelectDay: (date: string) => void;
  layout: 'row' | 'grid' | 'agenda';
}) {
  const { t } = useTranslation('jobs');
  const { formatDate } = useLocaleFormatters();

  return (
    <div
      data-testid="availability-grid"
      className={cn(
        layout === 'grid' ? 'grid grid-cols-7 gap-1.5' : layout === 'agenda' ? 'flex flex-col gap-1.5' : 'flex gap-1.5 overflow-x-auto pb-1',
      )}
    >
      {days.map((day) => {
        const disabled = day.state === 'full' || day.state === 'closed';
        const isSelected = day.date === selectedDate;
        const isPreferred = day.date === preferredDate;
        const dateObj = new Date(`${day.date}T00:00:00`);
        const detail =
          day.state !== 'closed' ? (
            <>
              <span className="text-[10px]">{t('scheduling.baysFree', { free: day.baysFreeAtPeak, total: day.bayCount })}</span>
              <span className="text-[10px]">{t('scheduling.installerHoursFree', { hours: (day.installerMinutesFree / 60).toFixed(1) })}</span>
            </>
          ) : (
            <span className="text-[10px]">{t('scheduling.closedDay')}</span>
          );
        const preferredBadge = isPreferred ? (
          <span className="w-fit rounded-full bg-accent/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-accent-ink">
            {t('scheduling.customerPreferred')}
          </span>
        ) : null;

        return (
          <button
            key={day.date}
            type="button"
            data-testid={`day-cell-${day.date}`}
            data-state={day.state}
            disabled={disabled}
            onClick={() => onSelectDay(day.date)}
            className={cn(
              'flex flex-none border p-2 text-left text-xs',
              layout === 'agenda' ? 'w-full flex-row items-center gap-3' : 'flex-col gap-0.5',
              layout === 'row' ? 'w-[92px]' : layout === 'grid' ? 'w-full' : '',
              STATE_STYLE[day.state],
              isSelected ? 'ring-2 ring-accent ring-inset' : '',
            )}
          >
            {layout === 'agenda' ? (
              <>
                <span className="flex w-12 flex-none flex-col items-center">
                  <span className="font-mono text-sm font-bold">{formatDate(dateObj, 'd')}</span>
                  <span className="text-[10px]">{formatDate(dateObj, 'EEE')}</span>
                </span>
                <span className="flex flex-1 flex-col gap-0.5">
                  {detail}
                  {preferredBadge}
                </span>
              </>
            ) : (
              <>
                <span className="font-mono text-sm font-bold">{formatDate(dateObj, 'd')}</span>
                <span className="text-[10px]">{formatDate(dateObj, 'EEE')}</span>
                {detail}
                {preferredBadge}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
